import { Link } from "react-router-dom";
import { ContentPageLayout } from "@/components/seo/ContentPageLayout";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { ArrowRight, CheckCircle } from "lucide-react";

const BASE = "https://sorvaine.com";

const faqItems = [
  { question: "Does Sorvaine support UK public sector RFPs?", answer: "Yes. Sorvaine is built to handle UK public sector procurement, including Crown Commercial Service frameworks, G-Cloud, Digital Outcomes, and standard ITT/RFP formats published via Find a Tender and Contracts Finder." },
  { question: "Can Sorvaine handle social value and PPN compliance?", answer: "Yes. Sorvaine extracts every requirement from a tender — including social value criteria, PPN 06/20 weightings, and Carbon Reduction Plan obligations — and maps them to your response sections." },
  { question: "Is Sorvaine suitable for SME bidders?", answer: "Yes. SMEs often benefit most because they lack a dedicated bid team. Sorvaine compresses the response cycle so a single bid manager can ship more bids per quarter without compromising quality." },
];

export default function Uk() {
  return (
    <ContentPageLayout
      title="RFP Response Software for UK Bid Teams | Sorvaine"
      description="Sorvaine helps UK bid managers respond to public sector and enterprise RFPs faster with AI. Built for UK procurement frameworks."
      canonical={`${BASE}/uk`}
      breadcrumbs={[
        { name: "Home", url: BASE },
        { name: "UK", url: `${BASE}/uk` },
      ]}
      ctaHeading="Win more UK bids — faster"
      ctaDescription="See how Sorvaine fits into your bid team's workflow with a live demo on a real UK tender."
    >
      <SchemaMarkup type="faq" items={faqItems} />

      <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1]">
        Win more UK bids with AI-powered RFP response software
      </h1>
      <p className="mt-4 text-lg text-white/50 max-w-[640px]">
        Sorvaine is built for UK bid managers responding to public sector frameworks, enterprise RFPs, and complex tenders — without burning out the team.
      </p>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Built for UK procurement</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          UK bidding has its own rules. Crown Commercial Service frameworks, G-Cloud iterations, Digital Outcomes and Specialists, NHS frameworks, and local authority tenders each come with strict structural requirements, evaluation criteria, and disqualification thresholds. Miss a single mandatory criterion in a Find a Tender notice and your bid is non-compliant before evaluators read a word.
        </p>
        <p className="mt-3 text-white/50 leading-relaxed">
          Sorvaine reads the full ITT or RFP, extracts every requirement — including buried PPN obligations, social value weightings, and Carbon Reduction Plan asks — and maps them into a structured response outline that mirrors the buyer's framework.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Frameworks Sorvaine handles</h2>
        <ul className="mt-4 grid sm:grid-cols-2 gap-3">
          {[
            "G-Cloud (current and successor lots)",
            "Digital Outcomes & Specialists",
            "Crown Commercial Service frameworks",
            "NHS Shared Business Services",
            "ESPO, YPO, CCS Spark and similar",
            "Local authority OJEU-style tenders",
            "Find a Tender Service (FTS) opportunities",
            "Private sector enterprise RFPs",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-white/50">
              <CheckCircle className="w-4 h-4 mt-1 text-primary/60 shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Why UK bid teams choose Sorvaine</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Most UK bid managers we speak to are juggling three or four live tenders at once. They've inherited a SharePoint folder of past responses, a half-built content library in Excel, and an AE breathing down their neck for the next pricing iteration. The bottleneck is rarely capability — it's coordination, content reuse, and the manual grind of mapping every clause to a response.
        </p>
        <p className="mt-3 text-white/50 leading-relaxed">
          Sorvaine compresses that grind. Upload the tender PDF, and within minutes you have a section-by-section outline, a compliance matrix, and AI-generated first drafts trained on your previous winning bids. Your team spends time refining and pricing — not transcribing requirements into a spreadsheet at 11pm.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Compliance, not just speed</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Speed without compliance is a fast route to disqualification. Sorvaine flags every mandatory requirement — Cyber Essentials, ISO 27001, modern slavery statements, financial standing thresholds, insurance levels — and prevents you from marking a section "ready to submit" until each one is addressed and evidenced.
        </p>
        <p className="mt-3 text-white/50 leading-relaxed">
          For social value, Sorvaine maps the buyer's specific PPN weightings (06/20 model) to your response and flags gaps where a quantified commitment is expected but missing.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Get started</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Whether you're a 2-person consultancy chasing G-Cloud lots or a 50-person bid team responding to NHS frameworks every week, Sorvaine adapts to how your team already works. <Link to="/signup" className="text-primary hover:underline">Get started</Link> or <Link to="/book-demo" className="text-primary hover:underline">book a demo</Link> on one of your live tenders.
        </p>
        <div className="mt-6">
          <Link to="/signup">
            <button className="inline-flex items-center justify-center px-8 h-11 rounded-md bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-all duration-200 hover:-translate-y-[1px] active:scale-[0.97]">
              Get started
              <ArrowRight className="ml-2 w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">FAQ</h2>
        <div className="mt-6 space-y-6">
          {faqItems.map((item, i) => (
            <div key={i}>
              <h3 className="text-sm font-semibold text-white/70">{item.question}</h3>
              <p className="mt-1.5 text-sm text-white/40 leading-relaxed">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>
    </ContentPageLayout>
  );
}
